import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Home, History, Settings, User, Stethoscope, Sun, Moon, LogOut, Package, Activity } from 'lucide-react';
import { Button } from './ui/button';
import { Toaster } from './ui/sonner';
import { HealthCrossLogo } from './HealthCrossLogo';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const isPatient = user?.role === 'patient';
  const isMedical = user?.role === 'medical_professional';
  const isAdmin = user?.role === 'admin';

  const patientNav = [
    { icon: Home, label: 'Home', path: '/dashboard' },
    { icon: Activity, label: 'Diagnose', path: '/symptoms' },
    { icon: History, label: 'History', path: '/history' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const medicalNav = [
    { icon: Home, label: 'Dashboard', path: '/medical' },
    { icon: Stethoscope, label: 'Questions', path: '/medical/questions' },
    { icon: Activity, label: 'Symptoms', path: '/medical/symptoms' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const adminNav = [
    { icon: Home, label: 'Dashboard', path: '/admin' },
    { icon: Package, label: 'Products', path: '/admin/products' },
    { icon: Stethoscope, label: 'Questions', path: '/admin/questions' },
    { icon: Activity, label: 'Symptoms', path: '/admin/symptoms' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const navItems = isAdmin ? adminNav : isMedical ? medicalNav : patientNav;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      {user && (
        <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate(user ? (user.role === 'admin' ? '/admin' : user.role === 'medical_professional' ? '/medical' : '/dashboard') : '/')}
              data-testid="logo-link"
            >
              <HealthCrossLogo className="w-10 h-10" iconClassName="w-5 h-5" />
              <span className="font-bold text-lg hidden sm:block">{t('appName')}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">{user.name}</span>
                {isAdmin && (
                  <span className="text-xs px-1.5 py-0.5 bg-primary text-primary-foreground rounded-full">{t('admin')}</span>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="rounded-full text-destructive hover:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Main content */}
      <main className={`${user ? 'pt-16 pb-24' : ''}`}>
        {children}
      </main>

      {/* Floating dock navigation */}
      {user && (
        <nav className="floating-dock" data-testid="bottom-nav">
          <div className="glass rounded-full px-2 py-2 flex items-center gap-1 shadow-lg">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? 'default' : 'ghost'}
                  size="icon"
                  className={`rounded-full transition-all ${isActive ? 'scale-110' : ''}`}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="sr-only">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </nav>
      )}

      <Toaster position="top-center" />
    </div>
  );
};
